import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  FaTools, FaBatteryFull, FaTachometerAlt, FaPaintBrush, 
  FaShieldAlt, FaSnowflake, FaCheck, FaClock, FaDollarSign,
  FaPhone, FaCalendarAlt, FaStar, FaArrowRight, FaWrench,
  FaBolt, FaCog, FaChevronDown
} from 'react-icons/fa';
import { golfCartServiceAPI } from '../services/golfCartServiceAPI';
import ThankYouModal from '../components/ThankYouModal';

const GolfCartServices = () => {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    cart_model: '',
    cart_year: '',
    preferred_date: '',
    preferred_time: '',
    notes: ''
  });

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    setLoading(true);
    const [categoriesRes, servicesRes] = await Promise.all([
      golfCartServiceAPI.getCategories(),
      golfCartServiceAPI.getServices()
    ]);
    
    if (categoriesRes.data) setCategories(categoriesRes.data);
    if (servicesRes.data) setServices(servicesRes.data);
    setLoading(false);
  };

  const getCategoryIcon = (categoryName) => {
    const icons = {
      'Maintenance & Repairs': FaTools,
      'Battery Services': FaBatteryFull,
      'Performance Upgrades': FaTachometerAlt,
      'Cosmetic & Customization': FaPaintBrush,
      'Safety & Inspection': FaShieldAlt,
      'Seasonal Services': FaSnowflake
    };
    return icons[categoryName] || FaWrench;
  };

  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(s => s.category_id === selectedCategory);

  const popularServices = services.filter(s => s.is_popular);

  const handleBookService = (service) => {
    setSelectedService(service);
    setShowAppointmentForm(true);
  };

  const handleSubmitAppointment = async (e) => {
    e.preventDefault();
    
    if (submitting) return; // Prevent double submission
    
    setSubmitting(true);
    
    const appointment = {
      ...formData,
      service_id: selectedService?.id || null,
      status: 'pending'
    };

    try {
      const { data, error } = await golfCartServiceAPI.createAppointment(appointment);
      if (error) {
        console.error('Appointment submission error:', error);
        // If database fails, still send email notification
      }
      
      // Send email notification using centralized service
      try {
        const { notificationService } = await import('../services/notificationService');
        await notificationService.sendGolfCartServiceNotification(formData);
        console.log('✅ Golf cart service notification sent');
      } catch (emailError) {
        console.error('Failed to send email notification:', emailError);
        // Don't let email failure block the success message
      }
      setShowAppointmentForm(false);
      setShowThankYou(true);

      // Fire Meta Pixel Lead event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: 'golf_cart_service', content_category: 'appointment' });
      }
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        cart_model: '',
        cart_year: '',
        preferred_date: '',
        preferred_time: '',
        notes: ''
      });
      setSelectedService(null);
      setSubmitting(false);
    } catch (error) {
      console.error('Error submitting appointment:', error);
      alert('Error submitting appointment. Please try again or call us directly at (817) 382-2316.');
      setSubmitting(false);
    }
  };

  const handleThankYouClose = () => {
    setShowThankYou(false);
  };

  return (
    <>
      {/* Thank You Modal with Google Ads Conversion Tracking */}
      <ThankYouModal
        isOpen={showThankYou}
        onClose={handleThankYouClose}
        title="Appointment Requested!"
        message="We will contact you shortly to confirm your golf cart service appointment."
        autoClose={true}
        autoCloseDelay={5000}
      />

      <Helmet>
        <title>Golf Cart Services | Paradise-Performance</title>
        <meta name="description" content="Professional golf cart maintenance, repairs, and customization services. From battery replacement to performance upgrades." />
              <link rel="canonical" href="https://paradise-performance.com/golf-cart-services" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-r from-gray-800 to-gray-900 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="container mx-auto px-4 relative z-10"
          >
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 mt-12">
                Professional Golf Cart Services
              </h1>
              <p className="text-xl mb-8 text-white/90">
                Expert maintenance, repairs, and customization for all golf cart makes and models
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  View Services
                </motion.button>
                <motion.a
                  href="tel:817-382-2316"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-primary-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-400 transition-colors flex items-center gap-2"
                >
                  <FaPhone /> Call Now
                </motion.a>
              </div>
            </div>
          </motion.div>

          {/* Decorative elements */}
          <div className="absolute top-20 left-10 w-60 h-60 bg-white/[.0125] rounded-full animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-52 h-52 bg-white/[.0165] rounded-full animate-pulse delay-300"></div>
        </section>

        {/* Quick Stats
        <section className="py-12 bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-center"
              >
                <div className="text-3xl font-bold text-primary-600">15+</div>
                <div className="text-gray-600">Years Experience</div>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-center"
              >
                <div className="text-3xl font-bold text-primary-600">1000+</div>
                <div className="text-gray-600">Carts Serviced</div>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-center"
              >
                <div className="text-3xl font-bold text-primary-600">5★</div>
                <div className="text-gray-600">Customer Rating</div>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-center"
              >
                <div className="text-3xl font-bold text-primary-600">24hr</div>
                <div className="text-gray-600">Turnaround Time</div>
              </motion.div>
            </div>
          </div>
        </section> */}

        {/* Popular Services */}
        {popularServices.length > 0 && (
          <section className="py-16 bg-gradient-to-br from-primary-50 to-white">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-12"
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Popular Services</h2>
                <p className="text-gray-600">Our most requested golf cart services</p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {popularServices.map((service, index) => (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                    className="bg-white rounded-xl shadow-lg p-6 border-2 border-primary-100"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 bg-primary-100 rounded-lg">
                        <FaStar className="w-6 h-6 text-primary-600" />
                      </div>
                      {service.price && (
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary-600">
                            ${service.price}
                          </div>
                          {service.price_type === 'fixed' && (
                            <div className="text-xs text-gray-500">Fixed Price</div>
                          )}
                        </div>
                      )}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{service.name}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{service.description}</p>
                    
                    {service.features && service.features.length > 0 && (
                      <ul className="mb-4 space-y-2">
                        {service.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <FaCheck className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleBookService(service)}
                      className="w-full bg-primary-600 text-white py-2 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                    >
                      Book Service
                    </motion.button>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Services */}
        <section id="services" className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">All Services</h2>
              <p className="text-gray-600 mb-8">Browse our complete range of golf cart services</p>
              
              {/* Category Filter */}
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`px-6 py-2 rounded-full font-medium transition-colors ${
                    selectedCategory === 'all'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Services
                </button>
                {categories.map(category => {
                  const Icon = getCategoryIcon(category.name);
                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-6 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
                        selectedCategory === category.id
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {category.name}
                    </button>
                  );
                })}
              </div>
            </motion.div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence mode="wait">
                  {filteredServices.map((service, index) => {
                    const CategoryIcon = service.category ? getCategoryIcon(service.category.name) : FaWrench;
                    return (
                      <motion.div
                        key={service.id}
                        layout
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        whileHover={{ y: -5 }}
                        className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow p-6 border border-gray-200"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="p-3 bg-gray-100 rounded-lg">
                            <CategoryIcon className="w-5 h-5 text-primary-600" />
                          </div>
                          {service.duration_minutes && (
                            <div className="flex items-center text-gray-500 text-sm">
                              <FaClock className="w-4 h-4 mr-1" />
                              {service.duration_minutes} min
                            </div>
                          )}
                        </div>

                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {service.name}
                        </h3>
                        
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {service.description}
                        </p>

                        {service.features && service.features.length > 0 && (
                          <div className="mb-4">
                            <div className="flex flex-wrap gap-1">
                              {service.features.slice(0, 2).map((feature, idx) => (
                                <span 
                                  key={idx} 
                                  className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                                >
                                  {feature}
                                </span>
                              ))}
                              {service.features.length > 2 && (
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                  +{service.features.length - 2} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <div>
                            {service.price ? (
                              <div className="flex items-baseline">
                                <span className="text-2xl font-bold text-primary-600">
                                  ${service.price}
                                </span>
                                {service.price_type === 'hourly' && (
                                  <span className="text-sm text-gray-500 ml-1">/hr</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-lg font-semibold text-gray-700">
                                Quote on Request
                              </span>
                            )}
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleBookService(service)}
                            className="bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
                          >
                            Book <FaArrowRight className="w-3 h-3" />
                          </motion.button>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Paradise-Performance</h2>
              <p className="text-gray-600">Expert golf cart service you can trust</p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-lg p-6 text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaTools className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="font-semibold mb-2">Expert Technicians</h3>
                <p className="text-gray-600 text-sm">Automotive professionals with years of experience</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-lg p-6 text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaDollarSign className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="font-semibold mb-2">Fair Pricing</h3>
                <p className="text-gray-600 text-sm">Competitive rates with no hidden fees</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-lg p-6 text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaBolt className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="font-semibold mb-2">Fast Service</h3>
                <p className="text-gray-600 text-sm">Quick turnaround to get you back on course</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-lg p-6 text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaShieldAlt className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="font-semibold mb-2">Warranty</h3>
                <p className="text-gray-600 text-sm">Service warranty on all repairs</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-gray-800 to-gray-700">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Service Your Golf Cart?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Schedule your service appointment today and keep your cart running like new
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAppointmentForm(true)}
                  className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  <FaCalendarAlt /> Schedule Service
                </motion.button>
                <motion.a
                  href="tel:817-382-2316"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-primary-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-400 transition-colors flex items-center gap-2"
                >
                  <FaPhone /> (817) 382-2316
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Appointment Form Modal */}
        <AnimatePresence>
          {showAppointmentForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setShowAppointmentForm(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-lg p-4 md:p-6 max-w-md w-full max-h-[85vh] md:max-h-[90vh] overflow-y-auto mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-2xl font-bold mb-4">Schedule Service Appointment</h3>
                
                {selectedService && (
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
                    <div className="font-semibold text-primary-700">{selectedService.name}</div>
                    {selectedService.price && (
                      <div className="text-primary-600">${selectedService.price}</div>
                    )}
                  </div>
                )}

                <form onSubmit={handleSubmitAppointment}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.customer_name}
                        onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                        className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.customer_email}
                        onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                        className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.customer_phone}
                        onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                        className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cart Model
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., Club Car DS"
                          value={formData.cart_model}
                          onChange={(e) => setFormData({...formData, cart_model: e.target.value})}
                          className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cart Year
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., 2020"
                          value={formData.cart_year}
                          onChange={(e) => setFormData({...formData, cart_year: e.target.value})}
                          className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Preferred Date *
                        </label>
                        <input
                          type="date"
                          required
                          value={formData.preferred_date}
                          onChange={(e) => setFormData({...formData, preferred_date: e.target.value})}
                          className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Preferred Time
                        </label>
                        <select
                          value={formData.preferred_time}
                          onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
                          className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">Select Time</option>
                          <option value="Morning (8AM-12PM)">Morning (8AM-12PM)</option>
                          <option value="Afternoon (12PM-5PM)">Afternoon (12PM-5PM)</option>
                          <option value="Evening (5PM-7PM)">Evening (5PM-7PM)</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Notes
                      </label>
                      <textarea
                        rows="3"
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        placeholder="Describe any specific issues or requests..."
                        className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full sm:flex-1 bg-primary-600 text-white py-3 sm:py-2 rounded-lg font-semibold hover:bg-primary-700 transition-colors active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Submitting...' : 'Submit Appointment'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAppointmentForm(false)}
                      disabled={submitting}
                      className="w-full sm:flex-1 bg-gray-200 text-gray-700 py-3 sm:py-2 rounded-lg font-semibold hover:bg-gray-300 transition-colors active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};
export default GolfCartServices;